import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Empty from '@fmbt/ui/Empty';
import { Gallery } from '@fmbt/ui/layout';
import PageHeaderPusher from 'components/atoms/PageHeaderPusher';
import OperationCard from 'components/cards/OperationCard';
import BackOfficeBarnOwlAttachDrawer from 'components/drawers/BackOfficeBarnOwlAttachDrawer';
import BackOfficeBarnOwlConvertDrawer from 'components/drawers/BackOfficeBarnOwlConvertDrawer';
import BackOfficeBarnOwlCreateDrawer from 'components/drawers/BackOfficeBarnOwlCreateDrawer';
import BackOfficeIPCameraAddDrawer from 'components/drawers/BackOfficeIPCameraAddDrawer';
import FarmbotSecurityCameraIcon from 'components/icons/FarmbotSecurityCameraIcon';
import IntegrationIcon from 'components/icons/IntegrationIcon';
import { ErrorBoundary } from 'config/sentry';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { useReadyFlag } from 'utils/useReadyFlags';

type DrawerValue = (typeof SEARCH_PARAMS.DRAWER.values)[keyof typeof SEARCH_PARAMS.DRAWER.values];

function BackOfficeOperations() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const enableSelfServeDataTransfer = useReadyFlag('enableSelfServeDataTransfer');
  const enableBarnOwlIntegration = useReadyFlag('enableBarnOwlIntegration');

  const canProvisionIPCameras = useMyUserHasInternalPermission({
    resource: ProtectedResource.IP_CAMERA,
    action: ResourceAction.ADD
  });

  const openDrawer = (drawer: DrawerValue) => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.set(SEARCH_PARAMS.DRAWER.key, drawer);
      return newSearchParams;
    });
  };

  const operations = [
    ...enableSelfServeDataTransfer ? [{
      title: 'Data transfer',
      onClick: () => {
        navigate('/back-office/operations/data-transfer');
      },
      Icon: IntegrationIcon,
      caption: 'Transfer sample data from an old sensor to a new one'
    }] : [],
    ...canProvisionIPCameras ? [
      {
        title: 'Add IP Camera',
        onClick: () => openDrawer(SEARCH_PARAMS.DRAWER.values.ADD_IP_CAMERA),
        caption: 'For Farmbot Security Cameras manufactured by Land Watch',
        Icon: FarmbotSecurityCameraIcon
      },
      ...(enableBarnOwlIntegration ? [
        {
          title: 'Create Barn Owl account',
          onClick: () => openDrawer(SEARCH_PARAMS.DRAWER.values.CREATE_BARN_OWL_ACCOUNT),
          caption: 'Create a managed Barn Owl Affiliate account for one enterprise',
          Icon: FarmbotSecurityCameraIcon
        },
        {
          title: 'Convert Barn Owl account',
          onClick: () => openDrawer(SEARCH_PARAMS.DRAWER.values.CONVERT_BARN_OWL_ACCOUNT),
          caption: 'Convert an existing Barn Owl account and import every camera onto one site',
          Icon: FarmbotSecurityCameraIcon
        },
        {
          title: 'Attach Barn Owl camera',
          onClick: () => openDrawer(SEARCH_PARAMS.DRAWER.values.ATTACH_BARN_OWL_CAMERA),
          caption: 'Attach one camera to the enterprise managed Barn Owl account',
          Icon: FarmbotSecurityCameraIcon
        }
      ] : [])
    ] : []
  ];

  return (
    <ErrorBoundary>
      <PageHeaderPusher ds enterpriseScoped={false}>
        <BackOfficeIPCameraAddDrawer />
        <BackOfficeBarnOwlCreateDrawer />
        <BackOfficeBarnOwlConvertDrawer />
        <BackOfficeBarnOwlAttachDrawer />
        {operations.length === 0 ? (
          <Empty description="No allowed operations" />
        ) : (
          <Gallery>
            {operations.map((operation, index) => (
              <OperationCard
                key={index}
                title={operation.title}
                caption={operation.caption}
                Icon={operation.Icon}
                onClick={operation.onClick}
              />
            ))}
          </Gallery>
        )}
      </PageHeaderPusher>
    </ErrorBoundary>
  );
}

export default BackOfficeOperations;
